import React, { useState, useEffect } from "react";
import { Table, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  fetchAllMonthlyTimesheets,
  saveTeamDayEntry,
  showGeneralError,
  createTeamMonthlyTimesheets,
  createWeekDayEntries,
  saveTeamComment,
  deleteTeamWeeklyTimesheetEntry,
  exportTeamMonthlyTimesheet,
} from "actions";
import AddCommentModal from "../AddCommentModal";
import { Filter, InformationMsg } from "../";
import { DeleteModal, Excel } from "CommonComponent";
import { isEmpty } from "lodash";
import moment from "moment";
import TimeInputCustom from "../TimeInputCustom";

const List = ({
  extraFiltersState,
  setExtraFiltersState,
  currentMonth,
  setCurrentMonth,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const session = useSelector((s) => s.session);
  const weeklyTimesheetState = useSelector(
    (state) => state.weeklyTimesheetState
  );
  const object_viewed_id = session?.current_page_permissions?.object_id || "";

  const [data, setData] = useState([]);

  const [days, setDays] = useState([]);
  const [dates, setDates] = useState([]);
  const [commentItem, setCommentItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [monthValue, setMonthValue] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const monthYear = params.get("month_year");
    const programId = params.get("program_id");
    const userIds = params.get("user_ids");

    if (monthYear) {
      const newValues = {
        ...extraFiltersState.values,
        month_year: monthYear,
      };
      if (programId) {
        newValues.program_id = [programId];
      }
      if (userIds) {
        newValues.user_ids = userIds.split(","); // Assuming user_ids can be multiple, comma-separated
      }
      setExtraFiltersState((prev) => ({ ...prev, values: newValues }));
      filterTable(newValues);
    }
  }, [location.search]);

  useEffect(() => {
    if (weeklyTimesheetState.redirectToList) {
      if (!isEmpty(extraFiltersState.values.month_year)) {
        filterTable();
      }
    }
  }, [
    weeklyTimesheetState.redirectToList,
    extraFiltersState.values.month_year,
  ]);

  useEffect(() => {
    const weeklyTimesheets = weeklyTimesheetState.weeklyTimesheetsList;

    if (!Array.isArray(weeklyTimesheets) || weeklyTimesheets.length === 0) {
      setData([]);
      return;
    }

    const formattedData = [];

    weeklyTimesheets.forEach((programData) => {
      let isFirstTaskOfProgram = true;

      if (!Array.isArray(programData.users)) return;

      programData.users.forEach((userBlock) => {
        if (!Array.isArray(userBlock.tasks)) return;

        userBlock.tasks.forEach((taskItem) => {
          const processMonthForTask = (isOverTime) => {
            const monthStr = extraFiltersState.values.month_year || moment().format("YYYY-MM");
            const daysInMonth = moment(monthStr, "YYYY-MM").daysInMonth();
            const monthStartDate = moment(monthStr, "YYYY-MM").startOf("month");

            const dailyValues = Array.from({ length: daysInMonth }, (_, dayIndex) => {
              const workDate = monthStartDate.clone().add(dayIndex, "days");
              return {
                hours: "",
                minutes: "",
                work_date: workDate.format("YYYY-MM-DD"),
                week_end_date: workDate.clone().endOf("isoWeek").format("YYYY-MM-DD"),
                weekly_timesheet_id: null,
              };
            });

            let totalHours = 0;
            let comments = [];

            if (Array.isArray(taskItem.weeks)) {
              taskItem.weeks.forEach((week) => {
                const entry = isOverTime ? week.overtime_entry : week.main_entry;
                const weekEndDate = week.week_end_date;

                if (entry) {
                  // Bind the entry ID to all days in this week so the UI knows which weekly sheet to update
                  if (entry.id) {
                    dailyValues.forEach((dv) => {
                      if (dv.week_end_date === weekEndDate) {
                        dv.weekly_timesheet_id = entry.id;
                      }
                    });
                  }

                  if (Array.isArray(entry.details)) {
                    entry.details.forEach((detail) => {
                      const workDate = moment(detail.work_date, "YYYY-MM-DD");
                      if (workDate.isSame(monthStartDate, 'month')) {
                        const dayIndex = workDate.date() - 1; // 0-indexed day of month

                        if (dayIndex >= 0 && dayIndex < daysInMonth) {
                          const totalTime = parseFloat(detail.total_time ?? detail.hours ?? 0);
                          dailyValues[dayIndex] = {
                            ...dailyValues[dayIndex],
                            hours: Math.floor(totalTime).toString(),
                            minutes: Math.round((totalTime % 1) * 60).toString(),
                            weekly_timesheet_id: entry.id || dailyValues[dayIndex].weekly_timesheet_id,
                          };
                        }
                      }
                    });
                  }

                  totalHours += parseFloat(entry.total_hours || 0);
                  if (entry.comments) comments.push(entry.comments);
                }
              });
            }

            formattedData.push({
              program: isFirstTaskOfProgram
                ? programData.program?.program_name || ""
                : "",
              user_name: userBlock.user?.name || "N/A",
              category: taskItem.task?.category?.name || "N/A",
              task: taskItem.task?.name || "",
              values: dailyValues,
              program_id: programData.program?.id,
              task_id: taskItem.task?.id,
              category_id: taskItem.task?.category?.id || null,
              user_id: userBlock.user?.id,
              total: totalHours,
              is_over_time_entry: isOverTime,
              comment: comments.join(" | "),
              // Fallback IDs for month-level operations if needed
              weekly_timesheet_ids: taskItem.weeks?.map(w => w[isOverTime ? 'overtime_entry' : 'main_entry']?.id).filter(Boolean) || [],
            });

            isFirstTaskOfProgram = false;
          };

          const hasMain = Array.isArray(taskItem.weeks) && taskItem.weeks.some((w) => w.main_entry);
          const hasOvertime = Array.isArray(taskItem.weeks) && taskItem.weeks.some((w) => w.overtime_entry);
          
          if (hasMain || (!hasMain && !hasOvertime)) processMonthForTask(false);
          if (hasOvertime) processMonthForTask(true);
        });
      });
    });

    setData(formattedData);
  }, [weeklyTimesheetState.weeklyTimesheetsList]);

  const filterTable = (filters) => {
    const filterValues = filters || extraFiltersState.values;
    if (!isEmpty(filterValues.month_year)) {
      const monthYear = moment(filterValues.month_year, "YYYY-MM");
      const monthStartDate = monthYear.clone().startOf("month");
      const monthEndDate = monthYear.clone().endOf("month");
      const newDates = [];
      const newDays = [];
      let currentDate = monthStartDate.clone();
      while (currentDate.isSameOrBefore(monthEndDate)) {
        newDates.push(currentDate.format("M/DD"));
        newDays.push(currentDate.format("ddd").toUpperCase());
        currentDate.add(1, "days");
      }
      setDates(newDates);
      setDays(newDays);

      dispatch(
        fetchAllMonthlyTimesheets({
          ...filterValues,
          object_viewed_id,
        })
      );
    } else {
      dispatch({ type: "CLEAR_WEEKLY_TIMESHEETS" });
      setDates([]);
      setDays([]);
    }
  };

  useEffect(() => {
    if (currentMonth) {
      filterTable();
    }
  }, [currentMonth]);

  const handleValueChange = (rowIndex, dayIndex, { field, value }) => {
    const updatedData = [...data];
    updatedData[rowIndex].values[dayIndex][field] = value;

    // calculate total hours
    updatedData[rowIndex].total = updatedData[rowIndex].values.reduce(
      (sum, v) => {
        const hours = parseInt(v.hours || 0);
        const minutes = parseInt(v.minutes || 0);
        return sum + hours + minutes / 60;
      },
      0
    );

    setData(updatedData);
  };

  const handleShiftTimeUpdate = (rowIndex, dayIndex) => {
    const dayValue = data[rowIndex].values[dayIndex];
    const item = data[rowIndex];

    // Calculate total hours (hours + minutes/60)
    const hoursValue =
      dayValue.hours === "" || dayValue.hours === null || dayValue.hours === undefined
        ? 0
        : parseFloat(dayValue.hours || 0);
    const minutesValue =
      dayValue.minutes === "" || dayValue.minutes === null || dayValue.minutes === undefined
        ? 0
        : parseFloat(dayValue.minutes || 0);
    const totalHours = hoursValue + minutesValue / 60;

    // Prepare data for API
    const dayEntryData = {
      work_date: dayValue.work_date,
      total_time: totalHours,
      minutes: minutesValue,
      hours: hoursValue,
      user_id: item.user_id,
    };

    const targetId = dayValue.weekly_timesheet_id;

    if (!targetId) {
      // Call createWeekDayEntries fallback when no week id exists yet
      const payload = {
        program_id: item.program_id,
        task_id: item.task_id,
        user_id: item.user_id,
        week_end_date: dayValue.week_end_date,
        is_over_time_entry: item.is_over_time_entry,
        day_entry: dayEntryData
      };
      dispatch(createWeekDayEntries(payload, object_viewed_id));
      return;
    }

    dispatch(
      saveTeamDayEntry(targetId, dayEntryData, object_viewed_id)
    );
  };

  const addOverTime = (e, item) => {
    e.preventDefault();
    const overTimeData = {
      program_id: item.program_id,
      task_ids: [item.task_id],
      is_over_time_entry: true,
      month_year: extraFiltersState.values.month_year,
      user_ids: [item.user_id],  // Ensure this uses an array 'user_ids' as expected by the new endpoint
    };
    dispatch(
      createTeamMonthlyTimesheets(overTimeData, object_viewed_id)
    );
  };

  const handleAddComment = (comment) => {
    if (commentItem) {
      const commentData = { comment: comment };
      const targetId = commentItem.weekly_timesheet_id || (commentItem.weekly_timesheet_ids && commentItem.weekly_timesheet_ids[0]);
      
      if (!targetId) return;

      dispatch(
        saveTeamComment(
          targetId,
          commentData,
          object_viewed_id
        )
      )
        .then(() => {
          const updatedData = data.map((item) => {
            // Match against weekly_timesheet_ids as fallback since id might be null
            const isMatch = item.weekly_timesheet_id === targetId || 
                            (item.weekly_timesheet_ids && item.weekly_timesheet_ids.includes(targetId)) ||
                            item.weekly_timesheet_id === commentItem.weekly_timesheet_id;
                            
            if (isMatch) {
              return { ...item, comment: comment };
            }
            return item;
          });
          setData(updatedData);
          setShowComment(false);
          setCommentItem(null);
        })
        .catch(() => {
          // Error is already handled by the action
        });
    }
  };

  const [showComment, setShowComment] = useState(false);

  const handleTeamDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const confirmTeamDelete = () => {
    if (!deleteItem) return;
    const targetId = deleteItem.weekly_timesheet_id || (deleteItem.weekly_timesheet_ids && deleteItem.weekly_timesheet_ids[0]);

    if (!targetId) {
      setShowDeleteModal(false);
      setDeleteItem(null);
      return;
    }

    dispatch(
      deleteTeamWeeklyTimesheetEntry(
        targetId,
        object_viewed_id
      )
    )
      .then(() => {
        setShowDeleteModal(false);
        setDeleteItem(null);
        // Refresh the table after deletion
        if (!isEmpty(extraFiltersState.values.month_year)) {
          filterTable();
        }
      })
      .catch(() => {
        // Error is already handled by the action
        setShowDeleteModal(false);
        setDeleteItem(null);
      });
  };

  const handleExport = () => {
    dispatch(
      exportTeamMonthlyTimesheet(
        {
          user_ids: extraFiltersState.values.user_ids,
          program_id: extraFiltersState.values.program_id,
          task_ids: extraFiltersState.values.task_ids,
          month_year: extraFiltersState.values.month_year, // IMPORTANT
        },
        object_viewed_id
      )
    );
  };

  const hasTimeValue = (value) => {
    return (
      (value.hours && value.hours !== "") ||
      (value.minutes && value.minutes !== "")
    );
  };

  return (
    <>
      <div className="d-flex align-items-center justify-content-between p-4">
        <InformationMsg />

        <Excel handleExport={handleExport} />
      </div>
      <Filter
        onFilter={filterTable}
        extraFiltersState={extraFiltersState}
        setExtraFiltersState={setExtraFiltersState}
        currentMonth={currentMonth}
        setCurrentMonth={setCurrentMonth}
        monthValue={monthValue}
        setMonthValue={setMonthValue}
      />
      <div className="weekly-table-container td-border">
        <Table className="no-border">
          <thead>
            <tr>
              <th>Program Name</th>
              <th>User</th>
              <th>Category</th>
              <th>Task</th>
              {dates.map((date, index) => (
                <th key={index}>
                  {date} <br />{" "}
                  <span className="days-table">({days[index]})</span>
                </th>
              ))}
              <th>Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, rowIndex) => (
              <tr
                key={rowIndex}
                className={item.is_over_time_entry ? "over-time-entry" : ""}
              >
                <td className="program-name">{item.program}</td>
                <td>{item.user_name}</td>
                <td>{item.category}</td>
                <td>{item.task}</td>
                {item.values.map((value, dayIndex) => (
                  <td key={dayIndex}>
                    <TimeInputCustom
                      value={value}
                      rowIndex={rowIndex}
                      dayIndex={dayIndex}
                      handleValueChange={handleValueChange}
                      handleShiftTimeUpdate={handleShiftTimeUpdate}
                      hasTime={hasTimeValue(value)}
                    />
                  </td>
                ))}
                <td>{item.total}</td>
                <td>
                  <div className="d-flex align-items-center">
                    {!item.is_over_time_entry ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`overtime-tooltip-${rowIndex}`}>
                            Add Overtime
                          </Tooltip>
                        }
                      >
                        <img
                          src="/assets/images/time-icon.svg"
                          alt="Add Over Time"
                          className="me-2"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => addOverTime(e, item)}
                        />
                      </OverlayTrigger>
                    ) : (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`overtime-tooltip-${rowIndex}`}>
                            Add Overtime
                          </Tooltip>
                        }
                      >
                        <img
                          src="/assets/images/time-icon.svg"
                          alt="Overtime"
                          className="me-2"
                          style={{ cursor: "pointer" }}
                        />
                      </OverlayTrigger>
                    )}
                    {/*<OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`comment-tooltip-${rowIndex}`}>
                          {item.comment ? item.comment : "Add Comment"}
                        </Tooltip>
                      }
                    >
                      <img
                        src="/assets/images/comment-icon.svg"
                        alt="Comment"
                        onClick={() => {
                          setCommentItem(item);
                          setShowComment(true);
                        }}
                        style={{ cursor: "pointer" }}
                        className="me-2"
                      />
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`delete-tooltip-${rowIndex}`}>
                          Delete Entry
                        </Tooltip>
                      }
                    >
                      <img
                        src="/assets/images/delete-icon.svg"
                        alt="Delete"
                        onClick={() => handleTeamDelete(item)}
                        style={{ cursor: "pointer" }}
                      />
                    </OverlayTrigger>*/}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* comment modal */}
        <AddCommentModal
          show={showComment}
          handleClose={() => {
            setShowComment(false);
            setCommentItem(null);
          }}
          taskComment={commentItem?.comment || ""}
          handleAdd={handleAddComment}
          dates={dates}
        />

        {/* delete confirmation modal */}
        {showDeleteModal && (
          <DeleteModal
            show={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setDeleteItem(null);
            }}
            onDelete={confirmTeamDelete}
            message="Are you sure you want to delete this timesheet entry?"
          />
        )}
      </div>
    </>
  );
};

export default List;
